/**
 * The Post Office (Phase 4): every feature introduction the Post Office has
 * sent, and how many were opened or dismissed. Read from the ledger, not from
 * PostHog, so the numbers stay whole when analytics is down or unset.
 */

import { useAdminAnnouncementsQuery } from './api';

const FEATURE_LABELS: Record<string, string> = {
  invites: 'Invite links',
  postage: 'Postage classes',
  vault: 'The secret code',
  photos: 'Photographs',
  birthday: 'Birthday note',
};

const CHANNEL_LABELS: Record<string, string> = {
  letter: 'Letter',
  notice: 'Notice',
};

function rate(opened: number, sent: number): string {
  if (!sent) return '—';
  return `${Math.round((opened / sent) * 100)}%`;
}

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

export function AdminAnnouncements() {
  const { data, error, isLoading, refetch } = useAdminAnnouncementsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  if (isLoading) return <p className="admin__empty">Loading…</p>;
  if (error || !data) {
    return (
      <p className="admin__empty">
        Couldn’t load the Post Office ledger.{' '}
        <button type="button" className="linkish" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }

  return (
    <div className="admin-postage">
      <div className="admin-stats">
        <div className="admin-stat">
          <span className="admin-stat__value">{data.totals.sent}</span>
          <span className="admin-stat__label">Introductions sent</span>
          <span className="admin-stat__note">one per member per feature, ever</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__value">{data.totals.opened}</span>
          <span className="admin-stat__label">Opened or dismissed</span>
          <span className="admin-stat__note">first open only; re-reads are not counted</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__value">{rate(data.totals.opened, data.totals.sent)}</span>
          <span className="admin-stat__label">Seen</span>
        </div>
      </div>

      <div className="admin-table__wrap">
        <table className="admin-table">
          <caption className="admin-table__caption">By feature</caption>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Channel</th>
              <th scope="col">Sent</th>
              <th scope="col">Opened</th>
              <th scope="col">Seen</th>
              <th scope="col">Last sent</th>
            </tr>
          </thead>
          <tbody>
            {data.features.map((f) => (
              <tr key={f.key}>
                <td>{FEATURE_LABELS[f.key] ?? f.key}</td>
                <td>{CHANNEL_LABELS[f.channel] ?? f.channel}</td>
                <td>{f.sent}</td>
                <td>{f.opened}</td>
                <td>{rate(f.opened, f.sent)}</td>
                <td>{day(f.lastSentAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.totals.sent === 0 && (
        <p className="admin__empty">Nothing has gone out yet. The Post Office waits for the right moment.</p>
      )}
    </div>
  );
}
